import React from 'react'
import { Box, Checkbox, Grid, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { FlagsIconSeeker, IconSeeker } from '@ui-seeker/core'

import { AppButtonIcon, AppCardTitle, AppProviderChips, AppRetailerBlockContainer, AppValueChip } from '@components'
import AppOfficesBlockContainer from '@components/DataBlocks/OfficesContainer'
import { dsColorNew } from "@palette"
import { MAPPING_TYPE } from "Pages/User/CustomMapping/@constants"
import { acrissTable, categoriesTable, selectedMappingTab } from "Pages/User/CustomMapping/@signals"
import CustomMappingActions from "Pages/User/CustomMapping/Actions"


const ACRISS_ROWS = [
	{
		offices: ['Madrid T4', 'Barcelona El Prat'],
		countries: [{ code: 'ES', name: 'Spain' }],
		sites: ['PriceFly', 'RentalSpot'],
		acriss: 'CDMR',
		providers: 'DrivePro, FleetMax',
		carModel: 'Seat Ibiza',
		doors: '5',
		seats: '5',
		transmission: 'Manual',
		ac: 'Yes',
		owner: 'Company',
	},
	{
		offices: ['Malaga Costa del Sol'],
		countries: [{ code: 'ES', name: 'Spain' }],
		sites: ['BookRide'],
		acriss: 'ECMR',
		providers: 'CarFirst',
		carModel: 'Renault Clio',
		doors: '5',
		seats: '5',
		transmission: 'Manual',
		ac: 'Yes',
		owner: 'User',
	},
	{
		offices: [],
		countries: [{ code: 'GB', name: 'United Kingdom' }],
		sites: ['SwiftFleet', 'PriceFly'],
		acriss: 'FWAR',
		providers: 'Any',
		carModel: 'Ford Focus Estate',
		doors: '5',
		seats: '5',
		transmission: 'Automatic',
		ac: 'Yes',
		owner: 'Company',
	},
	{
		offices: ['Paris CDG'],
		countries: [{ code: 'FR', name: 'France' }],
		sites: [],
		acriss: 'IDAR',
		providers: 'SpeedCar, AutoRent',
		carModel: 'BMW 3 Series',
		doors: '4',
		seats: '5',
		transmission: 'Automatic',
		ac: 'Yes',
		owner: 'User',
	},
]

const CATEGORY_ROWS = [
	{
		offices: ['Madrid T4'],
		countries: [{ code: 'ES', name: 'Spain' }],
		sites: ['RentalSpot'],
		category: 'Economy',
		providers: 'FleetMax, CarFirst',
		carModel: 'Fiat 500',
		doors: '3',
		seats: '4',
		transmission: 'Manual',
		ac: 'Any',
		owner: 'Company',
	},
	{
		offices: [],
		countries: [{ code: 'IT', name: 'Italy' }],
		sites: ['PriceFly'],
		category: 'Compact',
		providers: 'Any',
		carModel: 'VW Golf',
		doors: '5',
		seats: '5',
		transmission: 'Any',
		ac: 'Yes',
		owner: 'Company',
	},
	{
		offices: ['Tenerife South'],
		countries: [{ code: 'ES', name: 'Spain' }],
		sites: [],
		category: 'SUV',
		providers: 'DrivePro',
		carModel: 'Nissan Qashqai',
		doors: '5',
		seats: '5',
		transmission: 'Automatic',
		ac: 'Yes',
		owner: 'User',
	},
]

const Any = () => <Typography>Any</Typography>

// Each cell as the real column draws it (Table/columns)
function MappingRow({ row, codeField }) {
	return <TableRow>
		<TableCell padding="checkbox"><Checkbox size="small" tabIndex={-1} sx={{ color: dsColorNew.webBlue500 }}/></TableCell>
		<TableCell>
			{row.offices.length > 0 ? <AppOfficesBlockContainer rows={row.offices.map(name => ({ name }))} compact/> : <Any/>}
		</TableCell>
		<TableCell>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexDirection: 'column', gap: .5 }}>
				{row.countries.map(c => <Box key={c.code} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: .5 }}>
					<FlagsIconSeeker countryName={c.name}/>
					<Typography>{c.name}</Typography>
				</Box>)}
			</Box>
		</TableCell>
		<TableCell>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				{row.sites.length > 0 ? <AppRetailerBlockContainer rows={row.sites.map(name => ({ name }))} compact/> : <Any/>}
			</Box>
		</TableCell>
		<TableCell>
			{codeField === 'acriss'
				? <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					<AppValueChip tone="accent" sx={{ fontWeight: 600 }}>{row.acriss}</AppValueChip>
				</Box>
				: <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', ml: 1 }}>
					<Typography>{row[codeField]}</Typography>
				</Box>}
		</TableCell>
		<TableCell>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				{row.providers !== 'Any' ? <AppProviderChips providers={row.providers.split(', ')} sx={{ justifyContent: 'center' }}/> : 'Any'}
			</Box>
		</TableCell>
		<TableCell>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', ml: 1 }}>
				<Typography>{row.carModel}</Typography>
			</Box>
		</TableCell>
		{[row.doors, row.seats, row.transmission, row.ac, row.owner].map((value, i) => <TableCell key={i}>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<Typography>{value}</Typography>
			</Box>
		</TableCell>)}
		<TableCell sx={{ width: 50 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
				<AppButtonIcon icon="pen" title="Edit Mapping" tabIndex={-1}/>
				<AppButtonIcon icon="trash-can" title="Delete Mapping" tabIndex={-1}/>
			</Box>
		</TableCell>
	</TableRow>
}

// The mapping list as the real AppCrudTable draws it: card title, toolbar with the list's actions
// (the tour points at them), the theme's table head, zebra rows and the count footer
function MappingTable({ title, rows, codeLabel, codeField }) {
	const headers = [
		{ label: 'Offices' }, { label: 'Countries' }, { label: 'Sites' },
		{ label: codeLabel, align: codeField === 'acriss' ? 'center' : 'left' },
		{ label: 'Providers' }, { label: 'Car Model', align: 'left' },
		{ label: 'N˚ of Doors' }, { label: 'N˚ of seats' }, { label: 'Transmission' }, { label: 'A/C' }, { label: 'Owner' }, { label: 'Actions' },
	]

	return <Paper elevation={0} sx={{ backgroundColor: dsColorNew.white, p: 2 }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pl: 0.5, pb: 1.5 }}>
			<AppCardTitle icon="shuffle" title={title}/>
			<Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, color: dsColorNew.textMuted }}>
				{['magnifying-glass', 'download', 'filter'].map(icon => <IconButton key={icon} tabIndex={-1} sx={{ borderRadius: '8px' }}>
					<IconSeeker icon={icon} sx={{ fontSize: 16 }}/>
				</IconButton>)}
				<CustomMappingActions path={selectedMappingTab.value}/>
			</Box>
		</Box>
		<Box sx={{ overflowX: 'auto' }}>
			<Table sx={{
				// the head itself comes from the theme (MuiTableCell head); unaligned heads are centred as in the real table
				'& th': { py: 1.5, whiteSpace: 'nowrap', textAlign: 'center' },
				'& td': { fontSize: 13, color: dsColorNew.textDark, borderBottom: `1px solid ${dsColorNew.grayBorder}` },
				// the real cells' Typography variants at the data tables' size (MUIDataTableBodyCell)
				'& td .MuiTypography-body1, & td .MuiTypography-subtitle2': { fontSize: 13 },
				'& tbody tr:nth-of-type(even)': { backgroundColor: dsColorNew.tableRowAlt },
			}}>
				<TableHead>
					<TableRow>
						<TableCell padding="checkbox"><Checkbox size="small" tabIndex={-1} sx={{ color: alpha(dsColorNew.white, 0.7) }}/></TableCell>
						{headers.map(({ label, align }) => <TableCell key={label} sx={align ? { '&&': { textAlign: align } } : {}}>{label}</TableCell>)}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row, ri) => <MappingRow key={ri} row={row} codeField={codeField}/>)}
				</TableBody>
			</Table>
		</Box>
		<Typography sx={{ pt: 1.5, pr: 2, textAlign: 'right', fontSize: 14, color: dsColorNew.textDark }}>1-{rows.length} of {rows.length}</Typography>
	</Paper>
}


// Only the list of the open tab, as the page shows it, and only while that tab has no rows of its own
export default function MappingDemoData() {
	return <Grid container spacing={2}>
		{selectedMappingTab.value === MAPPING_TYPE.ACRISS && !acrissTable.value?.length && <Grid item xs={12}>
			<MappingTable
				title="Custom ACRISS Mapping List"
				rows={ACRISS_ROWS}
				codeLabel="ACRISS Code"
				codeField="acriss"
			/>
		</Grid>}
		{selectedMappingTab.value === MAPPING_TYPE.CATEGORY && !categoriesTable.value?.length && <Grid item xs={12}>
			<MappingTable
				title="Custom Categories Mapping List"
				rows={CATEGORY_ROWS}
				codeLabel="Category Normalized"
				codeField="category"
			/>
		</Grid>}
	</Grid>
}
